import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Stack, IconButton,
  Tooltip, TextField, InputAdornment, Select, MenuItem,
  FormControl, ClickAwayListener, Popper, Fade,
  Button, Slider, CircularProgress, Collapse,
} from '@mui/material';
import {
  Add, Close, ExpandMore, Search, SwapHoriz, Send, ContentCopy,
  CheckCircle, ExpandLess,
} from '@mui/icons-material';
import AIStrategyService from '../services/aiStrategyService';
import {
  buildLogicFromConditions,
  extractIndicatorsFromConditions,
  parseLogicToConditions,
} from './strategyUtils';

import {
  ORDER_FLOW_INDICATORS,
  ORDER_FLOW_SPOT_TEMPLATES,
  ORDER_FLOW_FUTURES_TEMPLATES,
  ORDER_FLOW_HEDGE_TEMPLATES,
  AI_ORDER_FLOW_PROMPTS,
  withOrderFlow,
  getOrderFlowTemplates,
} from './orderFlowExtension';


/* ═══════════════════════════════════════════════════════════════════════════════
   THEME — matched to trader_form.jsx
   ═══════════════════════════════════════════════════════════════════════════════ */

const C = {
  bg0: '#070a0f',
  bg1: '#070a0f',
  bg2: '#0f1218',
  bg3: '#0f1218',
  bg4: '#0b0e15',

  b0: 'rgba(255,255,255,0.06)',
  b1: 'rgba(255,255,255,0.06)',
  b2: 'rgba(255,255,255,0.1)',

  t1: '#f2f6ff',
  t2: '#e8edf7',
  t3: '#9aa6bd',
  t4: '#6f7c96',

  purple:   '#4d8fc7',
  green:    '#46b88a',
  red:      '#d97b72',
  blue:     '#4d8fc7',
  yellow:   '#d9a441',
  orange:   '#d9a441',

  long:     '#46b88a',
  longDim:  'rgba(70,184,138,0.08)',
  longBdr:  'rgba(70,184,138,0.25)',
  short:    '#d97b72',
  shortDim: 'rgba(217,123,114,0.08)',
  shortBdr: 'rgba(217,123,114,0.25)',
  ai:       '#4d8fc7',
  aiDim:    'rgba(77,143,199,0.08)',
  aiBdr:    'rgba(77,143,199,0.20)',

  // Futures-specific
  futuresWarn:    'rgba(217,164,65,0.08)',
  futuresWarnBdr: 'rgba(217,164,65,0.25)',

  // Order Flow accent (uses blue family)
  ofDim:  'rgba(77,143,199,0.06)',
  ofBdr:  'rgba(77,143,199,0.25)',

  mono: 'Roboto, "Helvetica Neue", Arial, sans-serif',
};


/* ═══════════════════════════════════════════════════════════════════════════════
   INDICATORS
   ═══════════════════════════════════════════════════════════════════════════════ */

const CANDLE_INDICATORS = {
  RSI:        { id: 'RSI',        label: 'RSI',         cat: 'Momentum',   params: [{ n: 'period', d: 14, min: 2, max: 100 }], range: [0,100] },
  MACD:       { id: 'MACD',       label: 'MACD',        cat: 'Trend',      params: [{ n: 'fast', d: 12 }, { n: 'slow', d: 26 }, { n: 'signal', d: 9 }] },
  EMA:        { id: 'EMA',        label: 'EMA',         cat: 'Trend',      params: [{ n: 'period', d: 20, min: 2, max: 500 }] },
  SMA:        { id: 'SMA',        label: 'SMA',         cat: 'Trend',      params: [{ n: 'period', d: 50, min: 2, max: 500 }] },
  BB_Upper:   { id: 'BB_Upper',   label: 'BB Upper',    cat: 'Volatility', params: [{ n: 'period', d: 20 }, { n: 'stdDev', d: 2 }] },
  BB_Lower:   { id: 'BB_Lower',   label: 'BB Lower',    cat: 'Volatility', params: [{ n: 'period', d: 20 }, { n: 'stdDev', d: 2 }] },
  Stochastic: { id: 'Stochastic', label: 'Stoch',       cat: 'Momentum',   params: [{ n: 'kPeriod', d: 14 }, { n: 'dPeriod', d: 3 }], range: [0,100] },
  ATR:        { id: 'ATR',        label: 'ATR',         cat: 'Volatility', params: [{ n: 'period', d: 14 }] },
  SuperTrend: { id: 'SuperTrend', label: 'SuperTrend',  cat: 'Trend',      params: [{ n: 'period', d: 10, min: 1, max: 100 }, { n: 'multiplier', d: 3, min: 0.5, max: 10 }] },
  StochRSI:   { id: 'StochRSI',   label: 'Stoch RSI',   cat: 'Momentum',   params: [{ n: 'rsiPeriod', d: 14, min: 2, max: 100 }, { n: 'stochPeriod', d: 14, min: 2, max: 100 }], range: [0,100] },
  Keltner_Upper: { id: 'Keltner_Upper', label: 'Keltner Upper', cat: 'Volatility', params: [{ n: 'emaPeriod', d: 20, min: 2, max: 200 }, { n: 'atrPeriod', d: 10, min: 1, max: 100 }, { n: 'multiplier', d: 2, min: 0.5, max: 5 }] },
  Keltner_Mid:   { id: 'Keltner_Mid',   label: 'Keltner Mid',   cat: 'Volatility', params: [{ n: 'emaPeriod', d: 20, min: 2, max: 200 }, { n: 'atrPeriod', d: 10, min: 1, max: 100 }, { n: 'multiplier', d: 2, min: 0.5, max: 5 }] },
  Keltner_Lower: { id: 'Keltner_Lower', label: 'Keltner Lower', cat: 'Volatility', params: [{ n: 'emaPeriod', d: 20, min: 2, max: 200 }, { n: 'atrPeriod', d: 10, min: 1, max: 100 }, { n: 'multiplier', d: 2, min: 0.5, max: 5 }] },
  VWAP:       { id: 'VWAP',       label: 'VWAP',        cat: 'Volume',     params: [] },
  Volume:     { id: 'Volume',     label: 'Volume',      cat: 'Volume',     params: [] },
  Price:      { id: 'Price',      label: 'Price',       cat: 'Price',      params: [] },
  MFI:        { id: 'MFI',        label: 'MFI',         cat: 'Volume',     params: [{ n: 'period', d: 14 }], range: [0,100] },
  OBV:        { id: 'OBV',        label: 'OBV',         cat: 'Volume',     params: [] },
  ADX:        { id: 'ADX',        label: 'ADX',         cat: 'Trend',      params: [{ n: 'period', d: 14 }] },

  // ── Price Change ───────────────────────────────────────────────
ROC:              { id: 'ROC',              label: 'ROC %',           cat: 'Price Change', params: [{ n: 'period', d: 14, min: 1, max: 500 }] },
PriceChangePct:   { id: 'PriceChangePct',   label: 'Price Δ %',       cat: 'Price Change', params: [{ n: 'period', d: 1,  min: 1, max: 500 }] },
PctFromHigh:      { id: 'PctFromHigh',      label: '% From High',     cat: 'Price Change', params: [{ n: 'lookback', d: 20, min: 1, max: 1000 }] },
PctFromLow:       { id: 'PctFromLow',       label: '% From Low',      cat: 'Price Change', params: [{ n: 'lookback', d: 20, min: 1, max: 1000 }] },
PctFromOpen:      { id: 'PctFromOpen',      label: '% From Open',     cat: 'Price Change', params: [] },
PctFromVWAP:      { id: 'PctFromVWAP',      label: '% From VWAP',     cat: 'Price Change', params: [] },
CumulativeMove:   { id: 'CumulativeMove',   label: 'Cumulative |Δ|',  cat: 'Price Change', params: [{ n: 'period', d: 10, min: 2, max: 500 }] },
Momentum:         { id: 'Momentum',         label: 'Momentum',        cat: 'Price Change', params: [{ n: 'period', d: 10, min: 1, max: 500 }] },
PPO:              { id: 'PPO',              label: 'PPO',             cat: 'Price Change', params: [{ n: 'fast', d: 12 }, { n: 'slow', d: 26 }] },
};

const TICK_INDICATORS = {
  tickPrice:       { id: 'tickPrice',       label: 'Tick Price',     cat: 'Tick', params: [] },
  tickDelta:       { id: 'tickDelta',       label: 'Tick Delta',     cat: 'Tick', params: [] },
  tickVolume:      { id: 'tickVolume',      label: 'Tick Vol',       cat: 'Tick', params: [] },
  tickTradeVolume: { id: 'tickTradeVolume', label: 'Tick TradeVol',  cat: 'Tick', params: [] },
  tickRSI:         { id: 'tickRSI',         label: 'Tick RSI',       cat: 'Tick', params: [{ n: 'period', d: 14, min: 2, max: 100 }], range: [0,100] },
  tickVWAP:        { id: 'tickVWAP',        label: 'Tick VWAP',      cat: 'Tick', params: [] },
};

const FUTURES_INDICATORS = {
  ...CANDLE_INDICATORS,

  // ── Funding Rate ────────────────────────────────────────────────
  FundingRate:           { id: 'FundingRate',           label: 'Funding Rate',        cat: 'Futures',         params: [],                                                                                         range: [-1, 1] },
  FundingRateEMA:        { id: 'FundingRateEMA',        label: 'Funding Rate EMA',    cat: 'Futures',         params: [{ n: 'period', d: 8, min: 2, max: 100 }],                                                  range: [-1, 1] },
  FundingRateSMA:        { id: 'FundingRateSMA',        label: 'Funding Rate SMA',    cat: 'Futures',         params: [{ n: 'period', d: 8, min: 2, max: 100 }],                                                  range: [-1, 1] },
  FundingRateCumulative: { id: 'FundingRateCumulative', label: 'Funding Cumulative',  cat: 'Futures',         params: [{ n: 'period', d: 24, min: 1, max: 500 }] },

  // ── Open Interest ───────────────────────────────────────────────
  OpenInterest:    { id: 'OpenInterest',    label: 'Open Interest',       cat: 'Futures OI',  params: [] },
  OI_Delta:        { id: 'OI_Delta',        label: 'OI Change %',         cat: 'Futures OI',  params: [{ n: 'period', d: 5, min: 1, max: 100 }] },
  OI_SMA:          { id: 'OI_SMA',          label: 'OI SMA',              cat: 'Futures OI',  params: [{ n: 'period', d: 14, min: 2, max: 200 }] },
  OI_EMA:          { id: 'OI_EMA',          label: 'OI EMA',              cat: 'Futures OI',  params: [{ n: 'period', d: 14, min: 2, max: 200 }] },
  OI_RSI:          { id: 'OI_RSI',          label: 'OI RSI',              cat: 'Futures OI',  params: [{ n: 'period', d: 14, min: 2, max: 100 }],                                                                range: [0, 100] },
  VWOI:            { id: 'VWOI',            label: 'Vol-Weighted OI',     cat: 'Futures OI',  params: [] },

  // ── Sentiment / Positioning ─────────────────────────────────────
  LongShortRatio:    { id: 'LongShortRatio',    label: 'L/S Ratio',        cat: 'Futures Sentiment', params: [] },
  LongShortRatioEMA: { id: 'LongShortRatioEMA', label: 'L/S Ratio EMA',    cat: 'Futures Sentiment', params: [{ n: 'period', d: 8, min: 2, max: 100 }] },

  // ── Mark / Index / Basis ────────────────────────────────────────
  MarkPrice:          { id: 'MarkPrice',          label: 'Mark Price',          cat: 'Futures Price', params: [] },
  MarkIndexSpread:    { id: 'MarkIndexSpread',    label: 'Mark-Index Spread %', cat: 'Futures Price', params: [] },
  MarkIndexSpreadEMA: { id: 'MarkIndexSpreadEMA', label: 'Mark-Index Sprd EMA', cat: 'Futures Price', params: [{ n: 'period', d: 14, min: 2, max: 100 }] },
  Basis:              { id: 'Basis',              label: 'Basis (Fut-Spot)',    cat: 'Futures Price', params: [] },
  BasisPct:           { id: 'BasisPct',           label: 'Basis %',             cat: 'Futures Price', params: [] },
  BasisPctEMA:        { id: 'BasisPctEMA',        label: 'Basis % EMA',         cat: 'Futures Price', params: [{ n: 'period', d: 14, min: 2, max: 100 }] },

  // ── Risk / Liquidation ──────────────────────────────────────────
  LiqPrice:             { id: 'LiqPrice',             label: 'Liq. Price',         cat: 'Futures Risk', params: [] },
  LiquidationDistance:  { id: 'LiquidationDistance',  label: 'Liq. Distance %',    cat: 'Futures Risk', params: [{ n: 'leverage', d: 10, min: 1, max: 125 }, { n: 'maintRate', d: 0.004, min: 0.001, max: 0.05 }] },
};

const TICK_ONLY_OF_INDICATORS = new Set([
  'LargeTradeBuy',
  'LargeTradeSell',
  'WhaleNetFlow',
  'Absorption',
  'VPIN',
  'KyleLambda',
]);

/* ═══════════════════════════════════════════════════════════════════════════════
   OPERATORS
   ═══════════════════════════════════════════════════════════════════════════════ */

const OPERATORS = [
  { value: '<',  sym: '<',  desc: 'Less than' },
  { value: '<=', sym: '≤',  desc: 'Less or equal' },
  { value: '>',  sym: '>',  desc: 'Greater than' },
  { value: '>=', sym: '≥',  desc: 'Greater or equal' },
  { value: '==', sym: '=',  desc: 'Equal to' },
];


/* ═══════════════════════════════════════════════════════════════════════════════
   TEMPLATES — spot + futures
   ═══════════════════════════════════════════════════════════════════════════════ */

export const SPOT_TEMPLATES = [
  {
    name: 'RSI Reversal',
    desc: 'Mean reversion — oversold / overbought',
    buy:  [{ ind: 'RSI', params: { period: 14 }, op: '<', val: '30' }],
    sell: [{ ind: 'RSI', params: { period: 14 }, op: '>', val: '70' }],
  },
  {
    name: 'EMA Crossover',
    desc: 'Fast EMA crosses slow EMA',
    buy:  [{ ind: 'EMA', params: { period: 9 }, op: '>', cmp: 'EMA', cmpParams: { period: 21 } }],
    sell: [{ ind: 'EMA', params: { period: 9 }, op: '<', cmp: 'EMA', cmpParams: { period: 21 } }],
  },
  {
    name: 'Bollinger Bounce',
    desc: 'Price touches lower / upper band',
    buy:  [{ ind: 'Price', op: '<=', cmp: 'BB_Lower', cmpParams: { period: 20, stdDev: 2 } }],
    sell: [{ ind: 'Price', op: '>=', cmp: 'BB_Upper', cmpParams: { period: 20, stdDev: 2 } }],
  },
  {
    name: 'VWAP + RSI',
    desc: 'Volume-weighted trend + momentum',
    buy: [
      { ind: 'Price', op: '>', cmp: 'VWAP' },
      { ind: 'RSI', params: { period: 14 }, op: '<', val: '50', combinator: 'AND' },
    ],
    sell: [{ ind: 'Price', op: '<', cmp: 'VWAP' }],
  },
  {
  name: 'Pullback Buy',
  desc: 'Buy when 5% off recent highs but trend still up',
  buy: [
    { ind: 'PctFromHigh', params: { lookback: 20 }, op: '<', val: '-5' },
    { ind: 'Price', op: '>', cmp: 'EMA', cmpParams: { period: 50 }, combinator: 'AND' },
  ],
  sell: [{ ind: 'PctFromHigh', params: { lookback: 20 }, op: '>=', val: '0' }],
}
];

const FUTURES_TEMPLATES = [
  {
    name: 'Funding Rate Arb',
    desc: 'Short when funding is extremely positive, long when negative',
    buy:  [{ ind: 'FundingRate', op: '<', val: '-0.01' }],
    sell: [{ ind: 'FundingRate', op: '>', val: '0.03' }],
  },
  {
    name: 'Funding EMA Trend',
    desc: 'Smoothed funding rate signals sustained directional pressure',
    buy: [
      { ind: 'FundingRateEMA', params: { period: 8 }, op: '<', val: '-0.005' },
      { ind: 'FundingRateCumulative', params: { period: 24 }, op: '<', val: '-0.05', combinator: 'AND' },
    ],
    sell: [
      { ind: 'FundingRateEMA', params: { period: 8 }, op: '>', val: '0.02' },
    ],
  },
  {
    name: 'OI Divergence',
    desc: 'Price up + OI rising = strong trend confirmation',
    buy: [
      { ind: 'Price', op: '>', cmp: 'EMA', cmpParams: { period: 20 } },
      { ind: 'OI_Delta', params: { period: 5 }, op: '>', val: '5', combinator: 'AND' },
    ],
    sell: [{ ind: 'OI_Delta', params: { period: 5 }, op: '<', val: '-5' }],
  },
  {
    name: 'OI Crowding + RSI',
    desc: 'OI RSI detects crowded positioning, price RSI confirms',
    buy: [
      { ind: 'OI_RSI', params: { period: 14 }, op: '<', val: '25' },
      { ind: 'RSI', params: { period: 14 }, op: '<', val: '35', combinator: 'AND' },
    ],
    sell: [
      { ind: 'OI_RSI', params: { period: 14 }, op: '>', val: '75' },
    ],
  },
  {
    name: 'Basis Mean Reversion',
    desc: 'Trade contango/backwardation extremes back to fair value',
    buy: [
      { ind: 'BasisPctEMA', params: { period: 14 }, op: '<', val: '-0.15' },
    ],
    sell: [
      { ind: 'BasisPctEMA', params: { period: 14 }, op: '>', val: '0.3' },
    ],
  },
  {
    name: 'RSI + Mark Price',
    desc: 'Enter oversold on mark price, exit on strength',
    buy:  [
      { ind: 'RSI', params: { period: 14 }, op: '<', val: '30' },
      { ind: 'Price', op: '<=', cmp: 'MarkPrice', combinator: 'AND' },
    ],
    sell: [{ ind: 'RSI', params: { period: 14 }, op: '>', val: '70' }],
  },
  {
    name: 'L/S Ratio Contrarian',
    desc: 'Go against the crowd when ratio is extreme',
    buy:  [{ ind: 'LongShortRatio', op: '<', val: '0.5' }],
    sell: [{ ind: 'LongShortRatio', op: '>', val: '2.0' }],
  },
  {
    name: 'Mark-Index Divergence',
    desc: 'Fade premium/discount when mark diverges from index',
    buy: [
      { ind: 'MarkIndexSpreadEMA', params: { period: 14 }, op: '<', val: '-0.1' },
    ],
    sell: [
      { ind: 'MarkIndexSpreadEMA', params: { period: 14 }, op: '>', val: '0.15' },
    ],
  },
  {
    name: 'Liq Distance Guard',
    desc: 'Only enter when liquidation is far, exit when close',
    buy: [
      { ind: 'RSI', params: { period: 14 }, op: '<', val: '35' },
      { ind: 'LiquidationDistance', params: { leverage: 10, maintRate: 0.004 }, op: '>', val: '15', combinator: 'AND' },
    ],
    sell: [
      { ind: 'LiquidationDistance', params: { leverage: 10, maintRate: 0.004 }, op: '<', val: '5' },
    ],
  },
];

const FUTURES_HEDGE_TEMPLATES = [
  {
    name: 'Dual EMA Hedge',
    desc: 'Trend-follow longs + mean-revert shorts',
    buy:       [{ ind: 'EMA', params: { period: 9 }, op: '>', cmp: 'EMA', cmpParams: { period: 21 } }],
    sell:      [{ ind: 'EMA', params: { period: 9 }, op: '<', cmp: 'EMA', cmpParams: { period: 21 } }],
    shortOpen: [{ ind: 'RSI', params: { period: 14 }, op: '>', val: '75' }],
    shortClose:[{ ind: 'RSI', params: { period: 14 }, op: '<', val: '45' }],
  },
  {
    name: 'Funding + OI Hedge',
    desc: 'Long on negative funding, short when OI drops',
    buy:       [{ ind: 'FundingRate', op: '<', val: '-0.01' }],
    sell:      [{ ind: 'FundingRate', op: '>', val: '0.01' }],
    shortOpen: [{ ind: 'OI_Delta', params: { period: 5 }, op: '<', val: '-10' }],
    shortClose:[{ ind: 'OI_Delta', params: { period: 5 }, op: '>', val: '5' }],
  },
  {
    name: 'Basis + L/S Hedge',
    desc: 'Long backwardation, short when crowd is long and basis premium',
    buy:       [{ ind: 'BasisPctEMA', params: { period: 14 }, op: '<', val: '-0.1' }],
    sell:      [{ ind: 'BasisPctEMA', params: { period: 14 }, op: '>', val: '0.05' }],
    shortOpen: [
      { ind: 'LongShortRatioEMA', params: { period: 8 }, op: '>', val: '2.0' },
      { ind: 'BasisPctEMA', params: { period: 14 }, op: '>', val: '0.2', combinator: 'AND' },
    ],
    shortClose:[{ ind: 'LongShortRatioEMA', params: { period: 8 }, op: '<', val: '1.2' }],
  },
  {
    name: 'OI Crowding Hedge',
    desc: 'Long deleveraging dips, short crowded rallies via OI RSI',
    buy:       [{ ind: 'OI_RSI', params: { period: 14 }, op: '<', val: '20' }],
    sell:      [{ ind: 'OI_RSI', params: { period: 14 }, op: '>', val: '60' }],
    shortOpen: [{ ind: 'OI_RSI', params: { period: 14 }, op: '>', val: '80' }],
    shortClose:[{ ind: 'OI_RSI', params: { period: 14 }, op: '<', val: '40' }],
  },
];


const AI_PROMPTS = [
  'Buy when RSI is oversold and price is above 50 SMA',
  'Golden cross: 50 SMA crosses above 200 SMA',
  'MACD bullish crossover with high volume confirmation',
  'Mean reversion: RSI below 25 buy, above 75 sell',
  'Trend following: price above EMA 20 and MACD positive',
];

const AI_FUTURES_PROMPTS = [
  'Open long when funding rate is very negative and RSI oversold',
  'Short when open interest is dropping and price below VWAP',
  'Hedge: long on EMA crossover, short on RSI overbought',
  'Enter when basis % EMA is below -0.1 and OI RSI under 30',
  'Long when cumulative funding is negative, exit when mark-index spread exceeds 0.15%',
  'Only open positions when liquidation distance is above 20%',
];


/* ═══════════════════════════════════════════════════════════════════════════════
   PANEL LABEL CONFIG — spot vs futures vs hedge
   ═══════════════════════════════════════════════════════════════════════════════ */

function getPanelConfig(type, marketType) {
  if (marketType === 'futures') {
    const FUTURES_CONFIG = {
      buy:         { label: 'OPEN LONG',   tag: 'ENTRY',  accent: C.green,  dimBg: C.longDim,  borderAccent: C.longBdr },
      sell:        { label: 'CLOSE LONG',  tag: 'EXIT',   accent: C.red,    dimBg: C.shortDim, borderAccent: C.shortBdr },
      short_open:  { label: 'OPEN SHORT',  tag: 'ENTRY',  accent: C.orange, dimBg: 'rgba(217,164,65,0.08)',  borderAccent: 'rgba(217,164,65,0.25)' },
      short_close: { label: 'CLOSE SHORT', tag: 'EXIT',   accent: C.blue,   dimBg: 'rgba(77,143,199,0.08)',  borderAccent: 'rgba(77,143,199,0.25)' },
    };
    return FUTURES_CONFIG[type] || FUTURES_CONFIG.buy;
  }

  // Spot defaults
  const isBuy = type === 'buy';
  return {
    label: isBuy ? 'ENTRY' : 'EXIT',
    tag:   isBuy ? 'LONG'  : 'SHORT',
    accent:       isBuy ? C.green : C.red,
    dimBg:        isBuy ? C.longDim : C.shortDim,
    borderAccent: isBuy ? C.longBdr : C.shortBdr,
  };
}


/* ═══════════════════════════════════════════════════════════════════════════════
   SHARED STYLES
   ═══════════════════════════════════════════════════════════════════════════════ */

const panelSx = {
  bgcolor: C.bg3,
  border: '1px solid',
  borderColor: C.b0,
  borderRadius: 2,
  overflow: 'hidden',
};

const headerBarSx = {
  display: 'flex',
  alignItems: 'center',
  px: 2,
  py: 1,
  bgcolor: C.bg1,
  borderBottom: '1px solid',
  borderColor: C.b0,
  minHeight: 40,
};

const microLabel = {
  fontFamily: C.mono,
  fontSize: 10,
  fontWeight: 600,
  color: C.t4,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  lineHeight: 1,
  mb: '5px',
  display: 'block',
  whiteSpace: 'nowrap',
};

const fieldSx = {
  bgcolor: C.bg1,
  borderRadius: 1.5,
  '& fieldset': { borderColor: C.b0, borderRadius: 1.5 },
  '&:hover fieldset': { borderColor: `${C.b2} !important` },
  '&.Mui-focused fieldset': { borderColor: `${C.b2} !important`, borderWidth: '1px !important' },
};

const mkSelectorSx = () => ({
  display: 'flex', alignItems: 'center', gap: 1,
  px: 1.5, height: 38,
  bgcolor: C.bg1,
  border: '1px solid', borderColor: C.b0,
  borderRadius: 1.5,
  cursor: 'pointer',
  transition: 'border-color 0.15s',
  '&:hover': { borderColor: C.b2 },
});


/* ═══════════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════════ */

const mkId = () => crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;

const getDefaults = (id, reg) => {
  const m = reg[id];
  if (!m?.params?.length) return {};
  return m.params.reduce((a, p) => ({ ...a, [p.n]: p.d }), {});
};

const fmtInd = (id, params, reg) => {
  const m = reg[id];
  if (!m) return id || '—';
  if (!m.params?.length) return m.label;
  const v = m.params.map(p => params?.[p.n] ?? p.d);
  return `${m.label}(${v.join(',')})`;
};

const buildPreview = (conds, reg) => {
  if (!conds?.length) return '';
  return conds.map((c, i) => {
    const l = fmtInd(c.indicator, c.params, reg);
    const r = c.compareIndicator
      ? fmtInd(c.compareIndicator, c.compareParams, reg)
      : (c.value ?? '');
    const pre = i > 0 && c.combinator ? ` ${c.combinator} ` : '';
    return `${pre}${l} ${c.operator || '?'} ${r}`;
  }).join('');
};

const makeEmptyCondition = (existingLength) => ({
  id: mkId(),
  indicator: '', params: {}, operator: '', value: '',
  compareIndicator: '', compareParams: {},
  combinator: existingLength > 0 ? 'AND' : null,
});

// Standalone template→conditions mapper. Mirrors the in-component `mapConditions`
// so callers (e.g. the AutoTrader "Quick Bot") can apply a preset like
// SPOT_TEMPLATES[0] and get fully-configured conditions ({indicator, operator,
// value, …}) without mounting the StrategyBuilder.
export const templateToConditions = (arr = []) =>
  (arr || []).map((c, i) => ({
    id: mkId(),
    indicator: c.ind,
    params: c.params || getDefaults(c.ind, CANDLE_INDICATORS),
    operator: c.op || '',
    value: c.val || '',
    compareIndicator: c.cmp || '',
    compareParams: c.cmpParams || (c.cmp ? getDefaults(c.cmp, CANDLE_INDICATORS) : {}),
    combinator: c.combinator || (i > 0 ? 'AND' : null),
  }));


/* ═══════════════════════════════════════════════════════════════════════════════
   INDICATOR PICKER
   ═══════════════════════════════════════════════════════════════════════════════ */

function IndicatorPicker({ anchorEl, open, indicators, onSelect, onClose }) {
  const [q, setQ] = useState('');
  const cats = useMemo(() => [...new Set(Object.values(indicators).map(i => i.cat))], [indicators]);
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return Object.values(indicators).filter(i => !s || i.label.toLowerCase().includes(s) || i.id.toLowerCase().includes(s));
  }, [indicators, q]);

  useEffect(() => { if (open) setQ(''); }, [open]);
  if (!open) return null;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Popper open={open} anchorEl={anchorEl} placement="bottom-start" transition style={{ zIndex: 1400 }}>
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={100}>
            <Paper
              elevation={8}
              sx={{
                mt: 0.5, width: 300, maxHeight: 340,
                bgcolor: C.bg1, border: '1px solid', borderColor: C.b2,
                borderRadius: 2, overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 1.25, borderBottom: '1px solid', borderColor: C.b0 }}>
                <TextField
                  size="small" fullWidth placeholder="Filter indicators…"
                  value={q} onChange={e => setQ(e.target.value)} autoFocus
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 14, color: C.t4 }} /></InputAdornment>,
                    sx: { ...fieldSx, fontFamily: C.mono, fontSize: 12, height: 32, '& input': { py: '5px' } },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
                {cats.map(cat => {
                  const items = filtered.filter(i => i.cat === cat);
                  if (!items.length) return null;
                  return (
                    <Box key={cat}>
                      <Typography sx={{
                        fontFamily: C.mono, fontSize: 10, fontWeight: 700, color: C.t4,
                        textTransform: 'uppercase', letterSpacing: '0.08em',
                        px: 1.5, pt: 1, pb: 0.25,
                      }}>
                        {cat}
                      </Typography>
                      {items.map(ind => (
                        <Box
                          key={ind.id}
                          onClick={() => { onSelect(ind.id); onClose(); }}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 0.75, mx: 0.5,
                            borderRadius: 1, cursor: 'pointer',
                            '&:hover': { bgcolor: C.bg4 },
                          }}
                        >
                          <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t2, fontWeight: 500 }}>
                            {ind.label}
                          </Typography>
                          {ind.params?.length > 0 && (
                            <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4, ml: 'auto' }}>
                              {ind.params.map(p => p.n).join(', ')}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  );
                })}
                {filtered.length === 0 && (
                  <Typography sx={{ p: 2, textAlign: 'center', color: C.t4, fontFamily: C.mono, fontSize: 12 }}>No match</Typography>
                )}
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>
    </ClickAwayListener>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   CONDITION ROW
   ═══════════════════════════════════════════════════════════════════════════════ */

function ConditionRow({ condition, index, panelConfig, indicators, onUpdate, onRemove, showCombinator }) {
  const [pickOpen, setPickOpen] = useState(false);
  const [cmpOpen, setCmpOpen] = useState(false);
  const pickRef = useRef(null);
  const cmpRef = useRef(null);

  const ind = indicators[condition.indicator];
  const cmpInd = indicators[condition.compareIndicator];

  return (
    <Box>
      {showCombinator && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          py: 0.75, gap: 0.75,
          borderBottom: '1px solid', borderColor: C.b0, bgcolor: C.bg1,
        }}>
          {['AND', 'OR'].map(c => (
            <Box
              key={c}
              onClick={() => onUpdate({ combinator: c })}
              sx={{
                px: 1.5, py: '3px',
                fontFamily: C.mono, fontSize: 11, fontWeight: 700,
                letterSpacing: '0.05em', cursor: 'pointer',
                borderRadius: 1, border: '1px solid',
                color: condition.combinator === c ? C.t1 : C.t4,
                bgcolor: condition.combinator === c ? C.bg4 : 'transparent',
                borderColor: condition.combinator === c ? C.b2 : C.b0,
                transition: 'all 0.15s',
                '&:hover': { borderColor: C.b2, color: C.t2, bgcolor: C.bg4 },
              }}
            >
              {c}
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '30px 1fr 130px 1fr 34px',
          gap: 1.25, alignItems: 'start',
          px: 2, py: 1.5,
          borderBottom: '1px solid', borderColor: C.b0,
          borderLeft: `3px solid ${panelConfig.borderAccent}`,
          transition: 'background 0.15s',
          '&:hover': { bgcolor: C.bg2 },
        }}
      >
        {/* Index */}
        <Box sx={{
          height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: C.mono, fontSize: 11, fontWeight: 600, color: C.t4,
        }}>
          {String(index + 1).padStart(2, '0')}
        </Box>

        {/* Indicator */}
        <Box>
          <Typography sx={microLabel}>INDICATOR</Typography>
          <Box ref={pickRef} onClick={() => setPickOpen(o => !o)} sx={mkSelectorSx()}>
            {ind ? (
              <Typography sx={{ fontFamily: C.mono, fontSize: 13, fontWeight: 600, color: C.t1 }}>{ind.label}</Typography>
            ) : (
              <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t4 }}>Select…</Typography>
            )}
            <ExpandMore sx={{ fontSize: 16, color: C.t4, ml: 'auto', flexShrink: 0 }} />
          </Box>
          <IndicatorPicker
            anchorEl={pickRef.current} open={pickOpen} indicators={indicators}
            onSelect={id => onUpdate({ indicator: id, params: getDefaults(id, indicators) })}
            onClose={() => setPickOpen(false)}
          />
          {ind?.params?.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {ind.params.map(p => (
                <Box key={p.n} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4 }}>{p.n}:</Typography>
                  <TextField
                    size="small" type="number"
                    value={condition.params?.[p.n] ?? p.d}
                    onChange={e => onUpdate({ params: { ...condition.params, [p.n]: Number(e.target.value) || p.d } })}
                    inputProps={{ min: p.min, max: p.max, step: Number.isInteger(p.d) ? 1 : 0.001, style: { fontFamily: C.mono, fontSize: 12, textAlign: 'center', padding: '3px 4px' } }}
                    sx={{ width: 64, '& .MuiInputBase-root': { ...fieldSx, height: 26 } }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Operator */}
        {condition.indicator ? (
          <Box>
            <Typography sx={microLabel}>OPERATOR</Typography>
            <FormControl size="small" fullWidth>
              <Select
                value={condition.operator || ''}
                onChange={e => onUpdate({ operator: e.target.value })}
                displayEmpty
                renderValue={(val) => {
                  if (!val) return <Typography sx={{ fontFamily: C.mono, fontSize: 13, color: C.t4 }}>—</Typography>;
                  const op = OPERATORS.find(o => o.value === val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 15, fontWeight: 700, color: C.t1, lineHeight: 1 }}>{op?.sym || val}</Typography>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t3, whiteSpace: 'nowrap' }}>{op?.desc || ''}</Typography>
                    </Box>
                  );
                }}
                sx={{
                  ...fieldSx, height: 38,
                  '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: '7px !important', px: '12px !important' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: C.bg1, border: '1px solid', borderColor: C.b2,
                      borderRadius: 2, mt: 0.5,
                    },
                  },
                }}
              >
                {OPERATORS.map(op => (
                  <MenuItem key={op.value} value={op.value} sx={{ py: 1, minHeight: 36 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <Typography sx={{ fontFamily: C.mono, fontWeight: 700, fontSize: 15, width: 22, textAlign: 'center', color: C.t1 }}>{op.sym}</Typography>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t3 }}>{op.desc}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        ) : <Box />}

        {/* Value / Compare */}
        {condition.indicator && condition.operator ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: '5px' }}>
              <Typography sx={{ ...microLabel, mb: 0 }}>
                {condition.compareIndicator ? 'COMPARE' : 'VALUE'}
              </Typography>
              <Tooltip title={condition.compareIndicator ? 'Switch to number' : 'Compare to indicator'} arrow placement="top">
                <IconButton
                  size="small"
                  onClick={() => {
                    if (condition.compareIndicator) {
                      onUpdate({ compareIndicator: '', compareParams: {}, value: '' });
                    } else {
                      onUpdate({ compareIndicator: 'EMA', compareParams: getDefaults('EMA', indicators), value: '' });
                    }
                  }}
                  sx={{ p: 0, width: 18, height: 18, color: C.t4, '&:hover': { color: C.t3 } }}
                >
                  <SwapHoriz sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Typography sx={{ fontFamily: C.mono, fontSize: 9, color: C.t4, ml: 'auto', letterSpacing: '0.04em' }}>
                {condition.compareIndicator ? 'IND' : 'NUM'}
              </Typography>
            </Box>

            {condition.compareIndicator ? (
              <>
                <Box ref={cmpRef} onClick={() => setCmpOpen(o => !o)} sx={mkSelectorSx()}>
                  {cmpInd ? (
                    <Typography sx={{ fontFamily: C.mono, fontSize: 13, fontWeight: 600, color: C.t1 }}>{cmpInd.label}</Typography>
                  ) : (
                    <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t4 }}>Select…</Typography>
                  )}
                  <ExpandMore sx={{ fontSize: 16, color: C.t4, ml: 'auto', flexShrink: 0 }} />
                </Box>
                <IndicatorPicker
                  anchorEl={cmpRef.current} open={cmpOpen} indicators={indicators}
                  onSelect={id => onUpdate({ compareIndicator: id, compareParams: getDefaults(id, indicators) })}
                  onClose={() => setCmpOpen(false)}
                />
                {cmpInd?.params?.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    {cmpInd.params.map(p => (
                      <Box key={p.n} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4 }}>{p.n}:</Typography>
                        <TextField
                          size="small" type="number"
                          value={condition.compareParams?.[p.n] ?? p.d}
                          onChange={e => onUpdate({ compareParams: { ...condition.compareParams, [p.n]: Number(e.target.value) || p.d } })}
                          inputProps={{ min: p.min, max: p.max, step: Number.isInteger(p.d) ? 1 : 0.001, style: { fontFamily: C.mono, fontSize: 12, textAlign: 'center', padding: '3px 4px' } }}
                          sx={{ width: 64, '& .MuiInputBase-root': { ...fieldSx, height: 26 } }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <TextField
                size="small" type="number" fullWidth placeholder="0"
                value={condition.value ?? ''}
                onChange={e => onUpdate({ value: e.target.value })}
                inputProps={{ step: ind?.range ? 0.01 : 'any', style: { fontFamily: C.mono, fontSize: 13, padding: '7px 12px' } }}
                sx={{ '& .MuiInputBase-root': { ...fieldSx, height: 38 } }}
              />
            )}
          </Box>
        ) : <Box />}

        {/* Remove */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: 38, justifyContent: 'center', pt: '20px' }}>
          <IconButton
            size="small" onClick={onRemove}
            sx={{ p: '4px', color: C.t4, '&:hover': { color: C.red, bgcolor: C.shortDim } }}
          >
            <Close sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   CONDITIONS PANEL
   FIX 4: Guard onChange with optional chaining in add/update/remove
   ═══════════════════════════════════════════════════════════════════════════════ */

function ConditionsPanel({ type, conditions, indicators, onChange, marketType }) {
  const cfg = useMemo(() => getPanelConfig(type, marketType), [type, marketType]);
  const preview = buildPreview(conditions, indicators);

  // FIX 4: Use optional chaining so missing onChange doesn't throw
  const add = () => onChange?.([...(conditions || []), makeEmptyCondition(conditions?.length || 0)]);

  const update = (i, u) => onChange?.((conditions || []).map((c, idx) => idx === i ? { ...c, ...u } : c));

  const remove = (i) => {
    const next = (conditions || []).filter((_, idx) => idx !== i);
    if (next.length > 0 && next[0].combinator) next[0] = { ...next[0], combinator: null };
    onChange?.(next);
  };

  return (
    <Paper elevation={0} sx={{ ...panelSx, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={headerBarSx}>
        <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: cfg.accent, mr: 1, flexShrink: 0, opacity: 0.7 }} />
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>{cfg.label}</Typography>
        <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4, ml: 1, letterSpacing: '0.03em' }}>{cfg.tag}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3, mr: 2 }}>
          {conditions?.length || 0} rule{(conditions?.length || 0) !== 1 ? 's' : ''}
        </Typography>
        <Box
          onClick={add}
          sx={{
            display: 'flex', alignItems: 'center', gap: 0.5,
            px: 1.25, py: '4px', border: '1px solid', borderColor: C.b0,
            borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s',
            '&:hover': { borderColor: C.b2, bgcolor: C.bg4 },
          }}
        >
          <Add sx={{ fontSize: 14, color: C.t3 }} />
          <Typography sx={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, color: C.t3 }}>ADD</Typography>
        </Box>
      </Box>

      {conditions?.length > 0 && (
        <Box sx={{
          display: 'grid', gridTemplateColumns: '30px 1fr 130px 1fr 34px',
          gap: 1.25, px: 2, py: 0.75, bgcolor: C.bg1,
          borderBottom: '1px solid', borderColor: C.b0,
        }}>
          <Box />
          <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Indicator</Typography>
          <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Operator</Typography>
          <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Value</Typography>
          <Box />
        </Box>
      )}

      <Box sx={{ flex: 1 }}>
        {(!conditions || conditions.length === 0) ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, gap: 1.5 }}>
            <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t4 }}>No {cfg.label.toLowerCase()} conditions defined</Typography>
            <Box
              onClick={add}
              sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                px: 2, py: 0.75, border: '1px solid', borderColor: `${cfg.accent}40`,
                borderRadius: 1.5, cursor: 'pointer',
                '&:hover': { bgcolor: cfg.dimBg, borderColor: cfg.accent },
              }}
            >
              <Add sx={{ fontSize: 15, color: cfg.accent }} />
              <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 600, color: cfg.accent }}>Add Rule</Typography>
            </Box>
          </Box>
        ) : (
          conditions.map((cond, i) => (
            <ConditionRow
              key={cond.id || i} condition={cond} index={i}
              panelConfig={cfg}
              indicators={indicators}
              onUpdate={u => update(i, u)}
              onRemove={() => remove(i)}
              showCombinator={i > 0}
            />
          ))
        )}
      </Box>

      {preview && (
        <Box sx={{ px: 2, py: 1.25, bgcolor: C.bg1, borderTop: '1px solid', borderColor: C.b0 }}>
          <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3, wordBreak: 'break-word', lineHeight: 1.7 }}>
            <Box component="span" sx={{ color: C.t4, mr: 1 }}>{'>'}</Box>
            {preview}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   AI STRATEGY PANEL
   FIX 7: Initialize aiService.current in useRef() directly, not only in useEffect
   PATCH: accepts orderFlowEnabled to extend prompt list and refinement chips
   ═══════════════════════════════════════════════════════════════════════════════ */

function AIStrategyPanel({
  onApplyStrategy, currentBuyLogic, currentSellLogic, interval, apiKey,
  expanded, onToggle, marketType, positionMode,
  orderFlowEnabled,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [copied, setCopied] = useState(null);
  const scrollRef = useRef(null);

  // FIX 7: Initialize in useRef directly so it's never null on first render
  const aiService = useRef(new AIStrategyService(apiKey || process.env.REACT_APP_OPENAI_API_KEY));
  useEffect(() => {
    aiService.current = new AIStrategyService(apiKey || process.env.REACT_APP_OPENAI_API_KEY);
  }, [apiKey]);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const isFutures = marketType === 'futures';
  const isHedge = isFutures && positionMode === 'hedge';

  // PATCH: combine prompt sets — base + order-flow when enabled
  const prompts = useMemo(() => [
    ...(isFutures ? AI_FUTURES_PROMPTS : AI_PROMPTS),
    ...(orderFlowEnabled ? AI_ORDER_FLOW_PROMPTS : []),
  ], [isFutures, orderFlowEnabled]);

  const addMsg = (role, content, meta = {}) => {
    setMessages(prev => [...prev, { id: mkId(), role, content, ts: new Date(), ...meta }]);
  };

  const submit = async (override = null) => {
    const prompt = override || input.trim();
    if (!prompt || isLoading) return;
    addMsg('user', prompt);
    setInput('');
    setIsLoading(true);
    try {
      const result = await aiService.current.generateStrategy(prompt, {
        interval,
        marketType: marketType || 'spot',
        positionMode: positionMode || 'one_way',
        orderFlowEnabled: !!orderFlowEnabled,
        existingBuyLogic: currentBuyLogic,
        existingSellLogic: currentSellLogic,
      });
      setLastResult(result);
      addMsg('assistant', result.explanation, {
        buyLogic: result.buyLogic, sellLogic: result.sellLogic,
        shortOpenLogic: result.shortOpenLogic, shortCloseLogic: result.shortCloseLogic,
        warnings: result.warnings, suggestions: result.suggestions,
        validation: result.validation, success: result.success,
      });
    } catch (err) {
      addMsg('assistant', `Error: ${err.message}`, { isError: true });
    } finally { setIsLoading(false); }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(prev => prev === label ? null : prev), 1500);
  };

  const fmtTime = (d) => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}:${String(d.getSeconds()).padStart(2,'0')}`;

  const renderLogicBlock = (logic, label, color, dimBg, borderColor, copyKey) => {
    if (!logic) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, bgcolor: dimBg, border: '1px solid', borderColor, borderRadius: 1.5 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: color, flexShrink: 0, opacity: 0.7 }} />
        <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 700, color: C.t4, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>{label}</Typography>
        <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t1, flex: 1, wordBreak: 'break-word' }}>{logic}</Typography>
        <Tooltip title={copied === copyKey ? 'Copied' : 'Copy'} arrow>
          <IconButton size="small" onClick={() => copy(logic, copyKey)} sx={{ p: '3px', color: C.t4, '&:hover': { color: C.t2 } }}>
            {copied === copyKey ? <CheckCircle sx={{ fontSize: 14 }} /> : <ContentCopy sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Paper elevation={0} sx={panelSx}>
      <Box onClick={onToggle} sx={{ ...headerBarSx, cursor: 'pointer', justifyContent: 'space-between', '&:hover': { bgcolor: C.bg2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: C.purple, flexShrink: 0, opacity: 0.7 }} />
          <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>AI ASSIST</Typography>
          <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4 }}>natural language → conditions</Typography>
          {isFutures && (
            <Chip size="small" label="FUTURES" sx={{ height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700, bgcolor: C.futuresWarn, color: C.yellow, border: '1px solid', borderColor: C.futuresWarnBdr }} />
          )}
          {orderFlowEnabled && (
            <Chip size="small" label="ORDER FLOW" sx={{ height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700, bgcolor: C.ofDim, color: C.blue, border: '1px solid', borderColor: C.ofBdr }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4 }}>{interval}</Typography>
          {expanded ? <ExpandLess sx={{ fontSize: 18, color: C.t3 }} /> : <ExpandMore sx={{ fontSize: 18, color: C.t3 }} />}
        </Box>
      </Box>

      <Collapse in={expanded} timeout={200}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 440 }}>
          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
            {messages.length === 0 ? (
              <Box sx={{ py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t3, textAlign: 'center', lineHeight: 1.7 }}>
                  Describe a {isFutures ? 'futures ' : ''}{orderFlowEnabled ? 'order-flow ' : ''}trading strategy in plain English.
                  <br />
                  AI will generate {isHedge ? 'long/short entry & exit' : 'entry/exit'} conditions you can apply directly.
                </Typography>
                <Box>
                  <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 600, color: C.t4, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1 }}>Examples</Typography>
                  {prompts.map((p, i) => (
                    <Box
                      key={i} onClick={() => setInput(p)}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1,
                        px: 1.5, py: 1, borderRadius: 1.5, cursor: 'pointer',
                        '&:hover': { bgcolor: C.bg4 },
                      }}
                    >
                      <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4, flexShrink: 0 }}>{'>'}</Typography>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t3, lineHeight: 1.5 }}>{p}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ) : (
              <Stack spacing={0}>
                {messages.map((msg) => (
                  <Box key={msg.id} sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4 }}>{fmtTime(msg.ts)}</Typography>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 700, color: msg.role === 'user' ? C.t2 : C.purple, letterSpacing: '0.04em' }}>
                        {msg.role === 'user' ? 'YOU' : 'AI'}
                      </Typography>
                    </Box>
                    <Box sx={{ pl: '56px' }}>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: msg.isError ? C.red : C.t2, lineHeight: 1.7, wordBreak: 'break-word' }}>
                        {msg.content}
                      </Typography>

                      {msg.role === 'assistant' && msg.success && (msg.buyLogic || msg.sellLogic || msg.shortOpenLogic || msg.shortCloseLogic) && (
                        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {renderLogicBlock(msg.buyLogic,        isFutures ? 'OPEN LONG'   : 'BUY',  C.green,  C.longDim,  C.longBdr,  'buy')}
                          {renderLogicBlock(msg.sellLogic,       isFutures ? 'CLOSE LONG'  : 'SELL', C.red,    C.shortDim, C.shortBdr, 'sell')}
                          {renderLogicBlock(msg.shortOpenLogic,  'OPEN SHORT',  C.orange, 'rgba(217,164,65,0.08)',  'rgba(217,164,65,0.25)',  'shortOpen')}
                          {renderLogicBlock(msg.shortCloseLogic, 'CLOSE SHORT', C.blue,   'rgba(77,143,199,0.08)',  'rgba(77,143,199,0.25)',  'shortClose')}

                          {msg.validation?.warnings?.length > 0 && (
                            <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'rgba(217,164,65,0.06)', border: '1px solid rgba(217,164,65,0.18)', borderRadius: 1.5 }}>
                              {msg.validation.warnings.map((w, i) => (
                                <Typography key={i} sx={{ fontFamily: C.mono, fontSize: 11, color: C.yellow, lineHeight: 1.6, opacity: 0.8 }}>! {w}</Typography>
                              ))}
                            </Box>
                          )}

                          {msg.suggestions?.length > 0 && (
                            <Box sx={{ px: 1.5, py: 0.5 }}>
                              {msg.suggestions.map((s, i) => (
                                <Typography key={i} sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3, lineHeight: 1.6 }}>— {s}</Typography>
                              ))}
                            </Box>
                          )}

                          <Box
                            onClick={() => onApplyStrategy?.({
                              buyLogic: msg.buyLogic,
                              sellLogic: msg.sellLogic,
                              shortOpenLogic: msg.shortOpenLogic,
                              shortCloseLogic: msg.shortCloseLogic,
                            })}
                            sx={{
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                              px: 2, py: 1,
                              bgcolor: C.aiDim, border: '1px solid', borderColor: C.aiBdr,
                              borderRadius: 1.5, cursor: 'pointer', transition: 'all 0.15s',
                              '&:hover': { bgcolor: 'rgba(77,143,199,0.14)', borderColor: C.purple },
                            }}
                          >
                            <CheckCircle sx={{ fontSize: 15, color: C.purple }} />
                            <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.purple, letterSpacing: '0.03em' }}>APPLY TO CONDITIONS</Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
                {isLoading && (
                  <Box sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 10, color: C.t4 }}>{fmtTime(new Date())}</Typography>
                      <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 700, color: C.purple }}>AI</Typography>
                    </Box>
                    <Box sx={{ pl: '56px', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={14} sx={{ color: C.purple }} />
                      <Typography sx={{ fontFamily: C.mono, fontSize: 12, color: C.t4 }}>Generating strategy…</Typography>
                    </Box>
                  </Box>
                )}
                <div ref={scrollRef} />
              </Stack>
            )}
          </Box>

          {/* Refinements */}
          {lastResult?.success && (
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: C.b0, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {[
                { label: 'Add stop loss', prompt: 'Add a stop loss condition to the sell logic' },
                { label: 'More conservative', prompt: 'Make this strategy more conservative with stricter conditions' },
                { label: 'Add volume filter', prompt: 'Add a volume filter to avoid low liquidity trades' },
                ...(isFutures ? [
                  { label: 'Add funding filter', prompt: 'Add funding rate check — only enter when funding supports the direction' },
                  { label: 'Add OI confirmation', prompt: 'Add open interest confirmation to filter weak entries' },
                  { label: 'Add basis filter', prompt: 'Add basis percentage check to avoid entering during extreme contango/backwardation' },
                  { label: 'Add liq guard', prompt: 'Add liquidation distance check — only enter when liq price is far enough away' },
                ] : []),
                ...(orderFlowEnabled ? [
                  { label: 'Add CVD confirm',     prompt: 'Add CVD confirmation — only enter when cumulative delta agrees with direction' },
                  { label: 'Add aggressor check', prompt: 'Add aggressor ratio filter to confirm buy/sell pressure' },
                  { label: 'Add whale filter',    prompt: 'Add whale net flow check — require large prints to support the direction' },
                  { label: 'Flow-based exit',     prompt: 'Replace the exit with order flow signals: aggressor flip and sell-volume spike' },
                ] : []),
              ].map((r, i) => (
                <Box
                  key={i} onClick={() => submit(r.prompt)}
                  sx={{
                    px: 1.25, py: '4px', border: '1px solid', borderColor: C.b0,
                    borderRadius: 1, cursor: 'pointer', transition: 'all 0.15s',
                    '&:hover': { borderColor: C.b2, bgcolor: C.bg4 },
                  }}
                >
                  <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t3 }}>{r.label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Input */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.25, borderTop: '1px solid', borderColor: C.b0, bgcolor: C.bg1 }}>
            <Typography sx={{ fontFamily: C.mono, fontSize: 13, color: C.t4, flexShrink: 0 }}>{'>'}</Typography>
            <TextField
              fullWidth size="small" placeholder={isFutures ? 'Describe your futures strategy…' : 'Describe your strategy…'}
              value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
              disabled={isLoading} multiline maxRows={2}
              InputProps={{ sx: { ...fieldSx, fontFamily: C.mono, fontSize: 13, '& textarea': { py: '7px', lineHeight: 1.5 } } }}
              sx={{ '& .MuiOutlinedInput-root': { minHeight: 36 } }}
            />
            <IconButton
              onClick={() => submit()} disabled={!input.trim() || isLoading}
              sx={{
                width: 36, height: 36, borderRadius: 1.5,
                bgcolor: input.trim() && !isLoading ? C.aiDim : 'transparent',
                border: '1px solid', borderColor: input.trim() && !isLoading ? C.aiBdr : C.b0,
                color: input.trim() && !isLoading ? C.purple : C.t4,
                '&:hover': { bgcolor: 'rgba(77,143,199,0.14)' },
                '&.Mui-disabled': { color: C.t4, borderColor: C.b0 },
              }}
            >
              {isLoading ? <CircularProgress size={16} sx={{ color: C.purple }} /> : <Send sx={{ fontSize: 16 }} />}
            </IconButton>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   ORDER FLOW TOGGLE
   ───────────────────────────────────────────────────────────────────────────────
   Compact bar that sits between the AI panel and presets when the parent has
   passed orderFlowEnabled control. Switches the indicator + template universe
   to include CVD, aggressor, whales, microstructure, etc.
   ═══════════════════════════════════════════════════════════════════════════════ */

function OrderFlowToggle({ enabled, onToggle, isFutures, isHedge }) {
const subtitle = enabled
    ? `${isHedge ? 'Hedge' : isFutures ? 'Futures' : 'Spot'} order flow active — whale/VPIN/Absorption need tick mode`
    : 'Enable to add CVD, aggressor, book imbalance (whale prints require tick mode)';

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 2, py: 1.25,
        bgcolor: enabled ? C.ofDim : C.bg3,
        border: '1px solid',
        borderColor: enabled ? C.ofBdr : C.b0,
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.15s',
        '&:hover': {
          borderColor: enabled ? C.blue : C.b2,
          bgcolor: enabled ? 'rgba(77,143,199,0.10)' : C.bg4,
        },
      }}
    >
      <Box sx={{
        width: 8, height: 8, borderRadius: 0.5,
        bgcolor: enabled ? C.blue : C.t4,
        flexShrink: 0, opacity: enabled ? 0.9 : 0.5,
        transition: 'all 0.15s',
      }} />
      <Typography sx={{
        fontFamily: C.mono, fontSize: 12, fontWeight: 700,
        color: enabled ? C.blue : C.t3,
        letterSpacing: '0.05em',
      }}>
        ORDER FLOW
      </Typography>
      <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4 }}>
        {subtitle}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Box
        sx={{
          position: 'relative',
          width: 36, height: 20, borderRadius: 10,
          bgcolor: enabled ? C.blue : C.b1,
          transition: 'background 0.15s',
          flexShrink: 0,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 2, left: enabled ? 18 : 2,
            width: 16, height: 16, borderRadius: '50%',
            bgcolor: C.t1, transition: 'left 0.15s',
          },
        }}
      />
    </Box>
  );
}


/* ═══════════════════════════════════════════════════════════════════════════════
   MAIN EXPORT
   FIX 5: applyTemplate always clears short conditions when not in hedge mode
   FIX 6: handleAIApply short logic guard is correct for one-way futures
   PATCH: orderFlowEnabled prop (with local fallback) drives indicator + template
          extension via withOrderFlow() and getOrderFlowTemplates()
   ═══════════════════════════════════════════════════════════════════════════════ */

export default function StrategyBuilder({
  buyConditions = [], sellConditions = [],
  onBuyConditionsChange, onSellConditionsChange,

  shortOpenConditions = [], shortCloseConditions = [],
  onShortOpenConditionsChange, onShortCloseConditionsChange,

  marketType = 'spot',
  positionMode = 'one_way',

  isTickMode = false,
  profitStop = '', lossStop = '',
  onProfitStopChange, onLossStopChange,
  onApplyStrategy, interval = '1H', apiKey,

  /* PATCH: order flow control (parent-controlled or local fallback) */
  orderFlowEnabled: orderFlowEnabledProp = false,
  onOrderFlowToggle,
}) {
  const [aiExpanded, setAiExpanded] = useState(false);
  const isFutures = marketType === 'futures';
  const isHedge = isFutures && positionMode === 'hedge';

  /* PATCH: local state fallback so the toggle works uncontrolled */
  const [orderFlowLocal, setOrderFlowLocal] = useState(orderFlowEnabledProp);
  const orderFlowEnabled = onOrderFlowToggle ? orderFlowEnabledProp : orderFlowLocal;
  const toggleOrderFlow = useCallback(() => {
    if (onOrderFlowToggle) onOrderFlowToggle(!orderFlowEnabled);
    else setOrderFlowLocal(p => !p);
  }, [onOrderFlowToggle, orderFlowEnabled]);

  /* Tick mode owns the registry. Otherwise pick spot/futures and overlay
     order flow indicators when the toggle is on. */
const INDICATORS = useMemo(() => {
  // Tick mode: base tick indicators + ALL order flow (yellow ones work here)
  if (isTickMode) {
    return { ...TICK_INDICATORS, ...ORDER_FLOW_INDICATORS };
  }

  // Candle/futures mode
  const base = isFutures ? FUTURES_INDICATORS : CANDLE_INDICATORS;
  if (!orderFlowEnabled) return base;

  // Candle + OF on: include order flow but hide the tick-only ones
  const withOF = withOrderFlow(base);
  return Object.fromEntries(
    Object.entries(withOF).filter(([id]) => !TICK_ONLY_OF_INDICATORS.has(id)),
  );
}, [isTickMode, isFutures, orderFlowEnabled]);
  /* Combine base templates with mode-appropriate order flow templates */
  const activeTemplates = useMemo(() => {
    if (isTickMode) return [];
    const base = isHedge   ? FUTURES_HEDGE_TEMPLATES
               : isFutures ? FUTURES_TEMPLATES
               :             SPOT_TEMPLATES;
    const ofTemplates = getOrderFlowTemplates({
      orderFlowEnabled, isTickMode, isFutures, isHedge,
    });
    return [...base, ...ofTemplates];
  }, [isTickMode, isFutures, isHedge, orderFlowEnabled]);

  const mapConditions = useCallback((arr) =>
    arr.map((c, i) => ({
      id: mkId(),
      indicator: c.ind, params: c.params || getDefaults(c.ind, INDICATORS),
      operator: c.op || '', value: c.val || '',
      compareIndicator: c.cmp || '', compareParams: c.cmpParams || (c.cmp ? getDefaults(c.cmp, INDICATORS) : {}),
      combinator: c.combinator || (i > 0 ? 'AND' : null),
    })),
  [INDICATORS]);

  // FIX 5: Always update short conditions — clear them when not in hedge mode
  // so stale conditions from a previous template don't persist after a mode switch
  const applyTemplate = useCallback((tpl) => {
    onBuyConditionsChange?.(mapConditions(tpl.buy || []));
    onSellConditionsChange?.(mapConditions(tpl.sell || []));
    onShortOpenConditionsChange?.(isHedge ? mapConditions(tpl.shortOpen || []) : []);
    onShortCloseConditionsChange?.(isHedge ? mapConditions(tpl.shortClose || []) : []);
  }, [mapConditions, isHedge, onBuyConditionsChange, onSellConditionsChange, onShortOpenConditionsChange, onShortCloseConditionsChange]);

  const handleAIApply = useCallback(({ buyLogic, sellLogic, shortOpenLogic, shortCloseLogic }) => {
    if (onApplyStrategy) {
      onApplyStrategy({ buyLogic, sellLogic, shortOpenLogic, shortCloseLogic });
      return;
    }
    if (buyLogic) {
      const parsed = parseLogicToConditions(buyLogic);
      if (parsed?.length) onBuyConditionsChange?.(parsed);
    }
    if (sellLogic) {
      const parsed = parseLogicToConditions(sellLogic);
      if (parsed?.length) onSellConditionsChange?.(parsed);
    }
    // Only apply short logic when in hedge mode — one-way futures doesn't use separate short panels
    if (shortOpenLogic && isHedge) {
      const parsed = parseLogicToConditions(shortOpenLogic);
      if (parsed?.length) onShortOpenConditionsChange?.(parsed);
    }
    if (shortCloseLogic && isHedge) {
      const parsed = parseLogicToConditions(shortCloseLogic);
      if (parsed?.length) onShortCloseConditionsChange?.(parsed);
    }
  }, [onApplyStrategy, isHedge, onBuyConditionsChange, onSellConditionsChange, onShortOpenConditionsChange, onShortCloseConditionsChange]);

  const currentBuyLogic = buildPreview(buyConditions, INDICATORS);
  const currentSellLogic = buildPreview(sellConditions, INDICATORS);
  const rrRatio = (profitStop && lossStop && Number(lossStop) > 0) ? (parseFloat(profitStop) / parseFloat(lossStop)).toFixed(1) : null;

  /* Count how many of the active templates are order-flow ones — used to label
     them visually inside the preset row */
  const ofTemplateCount = useMemo(
    () => getOrderFlowTemplates({ orderFlowEnabled, isTickMode, isFutures, isHedge }).length,
    [orderFlowEnabled, isTickMode, isFutures, isHedge],
  );
  const baseTemplateCount = activeTemplates.length - ofTemplateCount;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* AI */}
      <AIStrategyPanel
        expanded={aiExpanded} onToggle={() => setAiExpanded(p => !p)}
        onApplyStrategy={handleAIApply}
        currentBuyLogic={currentBuyLogic} currentSellLogic={currentSellLogic}
        interval={interval} apiKey={apiKey}
        marketType={marketType} positionMode={positionMode}
        orderFlowEnabled={orderFlowEnabled}
      />

      {/* Order Flow toggle — hidden in tick mode */}
      {!isTickMode && (
        <OrderFlowToggle
          enabled={orderFlowEnabled}
          onToggle={toggleOrderFlow}
          isFutures={isFutures}
          isHedge={isHedge}
        />
      )}

      {/* Presets */}
      {activeTemplates.length > 0 && (
        <Paper elevation={0} sx={panelSx}>
          <Box sx={headerBarSx}>
            <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>PRESETS</Typography>
            {isFutures && (
              <Chip size="small" label={isHedge ? 'HEDGE' : 'FUTURES'} sx={{ ml: 1, height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700, bgcolor: C.futuresWarn, color: C.yellow, border: '1px solid', borderColor: C.futuresWarnBdr }} />
            )}
            {orderFlowEnabled && ofTemplateCount > 0 && (
              <Chip size="small" label={`+${ofTemplateCount} OF`} sx={{ ml: 1, height: 18, fontFamily: C.mono, fontSize: 9, fontWeight: 700, bgcolor: C.ofDim, color: C.blue, border: '1px solid', borderColor: C.ofBdr }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {activeTemplates.map((tpl, i) => {
              const isOrderFlowTpl = i >= baseTemplateCount;
              return (
                <Box
                  key={i} onClick={() => applyTemplate(tpl)}
                  sx={{
                    flex: '1 1 170px', px: 2, py: 1.25, cursor: 'pointer',
                    borderRight: i < activeTemplates.length - 1 ? '1px solid' : 'none', borderColor: C.b0,
                    borderLeft: isOrderFlowTpl ? `2px solid ${C.ofBdr}` : 'none',
                    bgcolor: isOrderFlowTpl ? 'rgba(77,143,199,0.02)' : 'transparent',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: isOrderFlowTpl ? 'rgba(77,143,199,0.08)' : C.bg4 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: '2px' }}>
                    <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 600, color: C.t2, lineHeight: 1.4 }}>{tpl.name}</Typography>
                    {isOrderFlowTpl && (
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: C.blue, opacity: 0.7 }} />
                    )}
                  </Box>
                  <Typography sx={{ fontFamily: C.mono, fontSize: 11, color: C.t4, lineHeight: 1.4 }}>{tpl.desc}</Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
      )}

      {/* Mode badges */}
      {isTickMode && (
        <Box sx={{ px: 2, py: 1, bgcolor: C.bg3, border: '1px solid', borderColor: C.b0, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: C.t3, flexShrink: 0 }} />
    <Typography sx={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, color: C.t3 }}>
      TICK MODE — all order flow + tick indicators active
    </Typography>
  </Box>
      )}

      {isFutures && !isTickMode && (
        <Box sx={{ px: 2, py: 1, bgcolor: C.futuresWarn, border: '1px solid', borderColor: C.futuresWarnBdr, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: C.yellow, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: C.mono, fontSize: 11, fontWeight: 600, color: C.yellow }}>
            FUTURES MODE{isHedge ? ' — HEDGE' : ' — ONE-WAY'} — futures indicators available
          </Typography>
        </Box>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
           CONDITION PANELS — 2 for spot/one-way, 4 for hedge
         ═══════════════════════════════════════════════════════════════════ */}
      <Grid container spacing={2}>
        {/* Long Entry (buy) */}
        <Grid item xs={12} lg={6}>
          <ConditionsPanel
            type="buy"
            conditions={buyConditions}
            indicators={INDICATORS}
            onChange={onBuyConditionsChange}
            marketType={marketType}
          />
        </Grid>

        {/* Long Exit (sell) */}
        <Grid item xs={12} lg={6}>
          <ConditionsPanel
            type="sell"
            conditions={sellConditions}
            indicators={INDICATORS}
            onChange={onSellConditionsChange}
            marketType={marketType}
          />
        </Grid>

        {/* Hedge mode: Short Entry & Exit */}
        {isHedge && (
          <>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                <Box sx={{ flex: 1, height: '1px', bgcolor: C.b0 }} />
                <Typography sx={{ fontFamily: C.mono, fontSize: 10, fontWeight: 700, color: C.t4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  SHORT SIDE
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: C.b0 }} />
              </Box>
            </Grid>

            <Grid item xs={12} lg={6}>
              <ConditionsPanel
                type="short_open"
                conditions={shortOpenConditions}
                indicators={INDICATORS}
                onChange={onShortOpenConditionsChange}
                marketType={marketType}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <ConditionsPanel
                type="short_close"
                conditions={shortCloseConditions}
                indicators={INDICATORS}
                onChange={onShortCloseConditionsChange}
                marketType={marketType}
              />
            </Grid>
          </>
        )}
      </Grid>

      {/* Risk Mgmt */}
      <Paper elevation={0} sx={panelSx}>
        <Box sx={{ ...headerBarSx, justifyContent: 'space-between' }}>
          <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t1, letterSpacing: '0.05em' }}>RISK MGMT</Typography>
          {rrRatio && (
            <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t2 }}>
              R:R <Box component="span" sx={{ color: C.t1, ml: 0.75 }}>1:{rrRatio}</Box>
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {/* Take Profit */}
          <Box sx={{
            flex: '1 1 260px', p: 2.5,
            borderRight: { xs: 'none', sm: '1px solid' },
            borderBottom: { xs: '1px solid', sm: 'none' },
            borderColor: C.b0,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: C.green, flexShrink: 0, opacity: 0.7 }} />
              <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t2 }}>TAKE PROFIT</Typography>
              {profitStop && <Typography sx={{ fontFamily: C.mono, fontSize: 13, fontWeight: 700, color: C.green, ml: 'auto' }}>{profitStop}%</Typography>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small" type="number" value={profitStop} placeholder="—"
                onChange={e => onProfitStopChange?.(e.target.value)}
                inputProps={{ step: 0.1, min: 0, style: { fontFamily: C.mono, fontSize: 14, fontWeight: 600, padding: '7px 12px', textAlign: 'right' } }}
                InputProps={{
                  endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: C.mono, fontWeight: 600, color: C.t4, fontSize: 12 }}>%</Typography></InputAdornment>,
                  sx: { ...fieldSx, height: 38 },
                }}
                sx={{ width: 110 }}
              />
              <Slider
                value={Number(profitStop) || 0}
                onChange={(_, v) => onProfitStopChange?.(String(v))}
                min={0} max={50} step={0.5}
                sx={{
                  flex: 1, color: C.green,
                  '& .MuiSlider-thumb': { width: 12, height: 12, borderRadius: 1, boxShadow: 'none' },
                  '& .MuiSlider-track': { height: 3, borderRadius: 0 },
                  '& .MuiSlider-rail': { height: 3, bgcolor: C.b0, opacity: 1, borderRadius: 0 },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, ml: '110px', pl: 2 }}>
              {[0, 10, 20, 30, 40, 50].map(v => (
                <Typography key={v} sx={{ fontFamily: C.mono, fontSize: 9, color: C.t4 }}>{v}</Typography>
              ))}
            </Box>
          </Box>

          {/* Stop Loss */}
          <Box sx={{ flex: '1 1 260px', p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: 0.5, bgcolor: C.red, flexShrink: 0, opacity: 0.7 }} />
              <Typography sx={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700, color: C.t2 }}>STOP LOSS</Typography>
              {lossStop && <Typography sx={{ fontFamily: C.mono, fontSize: 13, fontWeight: 700, color: C.red, ml: 'auto' }}>{lossStop}%</Typography>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                size="small" type="number" value={lossStop} placeholder="—"
                onChange={e => onLossStopChange?.(e.target.value)}
                inputProps={{ step: 0.1, min: 0, style: { fontFamily: C.mono, fontSize: 14, fontWeight: 600, padding: '7px 12px', textAlign: 'right' } }}
                InputProps={{
                  endAdornment: <InputAdornment position="end"><Typography sx={{ fontFamily: C.mono, fontWeight: 600, color: C.t4, fontSize: 12 }}>%</Typography></InputAdornment>,
                  sx: { ...fieldSx, height: 38 },
                }}
                sx={{ width: 110 }}
              />
              <Slider
                value={Number(lossStop) || 0}
                onChange={(_, v) => onLossStopChange?.(String(v))}
                min={0} max={25} step={0.25}
                sx={{
                  flex: 1, color: C.red,
                  '& .MuiSlider-thumb': { width: 12, height: 12, borderRadius: 1, boxShadow: 'none' },
                  '& .MuiSlider-track': { height: 3, borderRadius: 0 },
                  '& .MuiSlider-rail': { height: 3, bgcolor: C.b0, opacity: 1, borderRadius: 0 },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75, ml: '110px', pl: 2 }}>
              {[0, 5, 10, 15, 20, 25].map(v => (
                <Typography key={v} sx={{ fontFamily: C.mono, fontSize: 9, color: C.t4 }}>{v}</Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export {
  ConditionsPanel,
  buildPreview,
  CANDLE_INDICATORS,
  TICK_INDICATORS,
  FUTURES_INDICATORS,
};